import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type {
  Conversation,
  RunStatusResponse,
  ArtifactLineageGraph,
  FeedbackCreate,
  TurnFeedback,
  Skill,
  SkillCreate,
  SkillUpdate,
  SkillsListResponse,
} from "~/components/assistant/types";
import { apiClient, apiMutate, isApiError } from "~/utils/apiClient";

// Dataset Assistant application ID
const appId = "DATASET_ASSISTANT";

/**
 * Extract a user-friendly error message from an apiClient error.
 * Handles 409 duplicate-name conflicts for skills with a friendly message.
 */
function parseValidationMessages(content: string): string | null {
  // Extract loc (field name) and msg pairs from Pydantic error string representation
  const matches = [...content.matchAll(/'loc':\s*\([^)]*'([^']+)'\).*?'msg':\s*'([^']+)'/g)];
  if (matches.length === 0) return null;

  const messages = matches.map(([, field, msg]) => {
    const cleanMsg = msg.replace(/^Value error,\s*/i, "");
    const label = field.charAt(0).toUpperCase() + field.slice(1);
    return `${label}: ${cleanMsg}`;
  });

  return messages.join("\n");
}

function formatSkillError(error: unknown, fallbackMessage: string): string {
  if (!isApiError(error)) return fallbackMessage;
  try {
    const body = JSON.parse(error.message);
    const content = body.detail ?? body.message ?? "";

    if (error.status === 409) {
      const match = content.match(/name '([^']+)'/i);
      if (match) {
        return `A skill named "${match[1]}" already exists. Please choose a different name.`;
      }
    }

    if (error.status === 422) {
      return parseValidationMessages(content) ?? fallbackMessage;
    }

    return content || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

const errMsg = (action: string) => (res: Response) => `Failed to ${action}: ${res.status}`;

/**
 * Creates an assistant API implementation for dataset context.
 * Uses the dataset assistant API endpoint.
 */
export function createDatasetAssistantApi(baseUrl: string): AssistantApi {
  return {
    baseUrl,

    async listConversations(datasetId: string): Promise<Conversation[]> {
      const params = new URLSearchParams({
        dataset_id: datasetId,
        app_id: appId,
      });
      const data = await apiClient<{ items: Conversation[] }>(
        `${baseUrl}/conversations?${params}`,
        {
          onErrorMessage: errMsg("load conversations"),
        }
      );

      // Sort by most recent first
      return data.items.sort(
        (a: Conversation, b: Conversation) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },

    async getRunStatus(conversationId: string): Promise<RunStatusResponse> {
      return apiClient<RunStatusResponse>(`${baseUrl}/conversations/${conversationId}/status`, {
        onErrorMessage: errMsg("get run status"),
      });
    },

    async createConversation(
      datasetId: string,
      datasetName?: string,
      agentId?: string | null
    ): Promise<Conversation> {
      return apiMutate<Conversation>(
        `${baseUrl}/conversations`,
        "POST",
        {
          dataset_id: datasetId,
          ...(agentId ? { agent_id: agentId } : { app_id: appId }),
          dataset_name: datasetName,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { onErrorMessage: errMsg("create conversation") }
      );
    },

    async deleteConversation(conversationId: string): Promise<void> {
      await apiMutate(`${baseUrl}/conversations/${conversationId}`, "DELETE", undefined, {
        onErrorMessage: errMsg("delete conversation"),
        expects204: true,
      });
    },

    async updateConversation(
      conversationId: string,
      update: { display_name: string }
    ): Promise<Conversation> {
      return apiMutate<Conversation>(
        `${baseUrl}/conversations/${conversationId}`,
        "PATCH",
        update,
        { onErrorMessage: errMsg("update conversation") }
      );
    },

    // Streaming endpoints — return the raw Response for incremental reads.
    // Cannot use apiClient here because it always parses the response as JSON.
    async loadChatHistory(conversationId: string): Promise<Response> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}/messages`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load chat history: ${response.status}`);
      }

      return response;
    },

    async sendMessage(
      conversationId: string,
      params: {
        prompt: string;
        contextId: string;
        clientMessageId?: string;
      }
    ): Promise<Response> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: params.prompt,
          client_message_id: params.clientMessageId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      return response;
    },

    async cancelMessage(conversationId: string): Promise<void> {
      await apiMutate(`${baseUrl}/conversations/${conversationId}/cancel`, "POST", undefined, {
        onErrorMessage: errMsg("cancel message"),
        expects204: true,
      });
    },

    async getArtifactLineage(
      conversationId: string,
      artifactId: string
    ): Promise<ArtifactLineageGraph> {
      return apiClient<ArtifactLineageGraph>(
        `${baseUrl}/conversations/${conversationId}/artifacts/${artifactId}/lineage`,
        { onErrorMessage: errMsg("load artifact lineage") }
      );
    },

    async getArtifactDownloadUrl(conversationId: string, artifactId: string): Promise<string> {
      const data = await apiClient<{ download_url: string }>(
        `${baseUrl}/conversations/${conversationId}/artifacts/${artifactId}`,
        { onErrorMessage: errMsg("get artifact download URL") }
      );
      return data.download_url;
    },

    async downloadArtifact(conversationId: string, artifactId: string, format: string) {
      return apiMutate<{
        download_url: string;
        file_name: string;
        content_type: string;
        status: string;
      }>(
        `${baseUrl}/conversations/${conversationId}/artifacts/${artifactId}/download`,
        "POST",
        { format },
        { onErrorMessage: errMsg("download artifact") }
      );
    },

    async putTurnFeedback(
      conversationId: string,
      turnId: string,
      feedback: FeedbackCreate
    ): Promise<TurnFeedback> {
      return apiMutate<TurnFeedback>(
        `${baseUrl}/conversations/${conversationId}/turns/${turnId}/feedback`,
        "PUT",
        feedback,
        { onErrorMessage: errMsg("create turn feedback") }
      );
    },

    async deleteTurnFeedback(conversationId: string, turnId: string): Promise<void> {
      try {
        await apiMutate(
          `${baseUrl}/conversations/${conversationId}/turns/${turnId}/feedback`,
          "DELETE",
          undefined,
          { onErrorMessage: errMsg("delete turn feedback"), expects204: true }
        );
      } catch (error) {
        // Treat 404 as success — feedback is already gone (idempotent delete)
        if (isApiError(error) && error.status === 404) return;
        throw error;
      }
    },

    async listSkills(datasetId?: string): Promise<SkillsListResponse> {
      const params = datasetId ? `?${new URLSearchParams({ dataset_id: datasetId })}` : "";
      return apiClient<SkillsListResponse>(`${baseUrl}/skills${params}`, {
        onErrorMessage: errMsg("load skills"),
      });
    },

    async getSkill(skillId: string): Promise<Skill> {
      return apiClient<Skill>(`${baseUrl}/skills/${skillId}`, {
        onErrorMessage: errMsg("load skill"),
      });
    },

    async createSkill(skill: SkillCreate): Promise<Skill> {
      try {
        return await apiMutate<Skill>(`${baseUrl}/skills`, "POST", skill);
      } catch (error) {
        throw new Error(formatSkillError(error, "Failed to create skill"));
      }
    },

    async updateSkill(skillId: string, skill: SkillUpdate): Promise<Skill> {
      try {
        return await apiMutate<Skill>(`${baseUrl}/skills/${skillId}`, "PATCH", skill);
      } catch (error) {
        throw new Error(formatSkillError(error, "Failed to update skill"));
      }
    },

    async deleteSkill(skillId: string): Promise<void> {
      await apiMutate(`${baseUrl}/skills/${skillId}`, "DELETE", undefined, {
        onErrorMessage: errMsg("delete skill"),
        expects204: true,
      });
    },

    async getUserPreferences() {
      return apiClient(`${baseUrl}/preferences`, {
        onErrorMessage: errMsg("get user preferences"),
      });
    },

    async updateUserPreferences(update: { accept_mcp_guidelines?: boolean }) {
      return apiMutate<{ mcp_guidelines_accepted_at: string | null }>(
        `${baseUrl}/preferences`,
        "PUT",
        update,
        { onErrorMessage: errMsg("update user preferences") }
      );
    },
  };
}
